import React from 'react';
import { productRange, BOTTLES } from '../mock';
import { Reveal, StaggerGroup, StaggerItem } from './Reveal';
import { motion } from 'framer-motion';

const bottleMask =
  'radial-gradient(ellipse 60% 90% at center, black 55%, rgba(0,0,0,0.85) 74%, transparent 100%)';

const Products = () => {
  return (
    <section
      id="products"
      className="relative py-24 lg:py-32 bg-gradient-to-b from-[#fafaf7] to-[#f4f6fb] overflow-hidden"
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-16">
        <Reveal>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-10 h-[1px] bg-[#c4a24a]" />
            <span className="text-[10px] font-medium tracking-[0.35em] text-[#c4a24a] uppercase">
              03 &mdash; The Collection
            </span>
          </div>
          <div className="grid lg:grid-cols-12 gap-6 items-end mb-16">
            <h2
              className="lg:col-span-8 text-[42px] lg:text-[64px] xl:text-[72px] leading-[1.03] font-medium text-[#0f2c6b] text-balance"
              style={{
                fontFamily: 'Fraunces, serif',
                fontVariationSettings: '"opsz" 144',
                letterSpacing: '-0.03em',
              }}
            >
              Our <span className="italic text-[#c4a24a] font-light">product</span> range.
            </h2>
            <p className="lg:col-span-4 text-[14.5px] leading-[1.8] text-slate-600 font-light">
              Six thoughtfully crafted variants across three signature sub-brands &mdash; Mini, Mist, Boat and Neo &mdash; plus institutional bulk packs.
            </p>
          </div>
        </Reveal>

        <StaggerGroup
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6"
          delay={0.09}
        >
          {productRange.map((product, idx) => (
            <StaggerItem key={idx}>
              <ProductCard product={product} />
            </StaggerItem>
          ))}

          {/* Bulk Packs - spans 2 cols */}
          <StaggerItem className="sm:col-span-3 lg:col-span-4">
            <BulkCard />
          </StaggerItem>
        </StaggerGroup>
      </div>
    </section>
  );
};

const ProductCard = ({ product }) => {
  return (
    <div className="group relative bg-white border border-slate-100 hover:border-[#c4a24a]/40 flex flex-col min-h-[500px] transition-all duration-500 hover:shadow-[0_20px_60px_-15px_rgba(15,44,107,0.18)] cursor-pointer overflow-hidden">
      {/* Hover accent line */}
      <motion.div
        className="absolute top-0 left-0 right-0 h-[2px] bg-[#c4a24a] origin-left z-10"
        initial={{ scaleX: 0 }}
        whileHover={{ scaleX: 1 }}
        transition={{ duration: 0.5 }}
      />

      {/* Sub-brand tag */}
      <div className="absolute top-4 left-4 z-10 text-[9px] tracking-[0.3em] text-[#c4a24a] uppercase font-medium">
        {product.name}
      </div>

      {/* Bottle stage with matching aesthetic background */}
      <div className="relative flex-1 flex items-center justify-center overflow-hidden">
        {/* Cream gradient background */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse at 50% 45%, #f5efdf 0%, #eae0c5 60%, #d9cca8 100%)',
          }}
        />
        {/* Warm light */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse at 50% 25%, rgba(255,240,200,0.35) 0%, transparent 60%)',
          }}
        />

        {/* Bottle image (masked to fade harsh edges into background) */}
        <img
          src={product.img}
          alt={`${product.name} ${product.size}${product.unit}`}
          className="relative max-h-[280px] max-w-[85%] w-auto h-auto object-contain group-hover:scale-105 group-hover:-translate-y-1 transition-transform duration-700 drop-shadow-[0_15px_30px_rgba(15,44,107,0.3)]"
          style={
            product.masked
              ? { WebkitMaskImage: bottleMask, maskImage: bottleMask }
              : undefined
          }
          loading="lazy"
        />

        {/* Floor reflection */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 w-32 h-3 bg-black/20 rounded-full blur-md" />
      </div>

      {/* Details */}
      <div className="w-full px-6 py-5 text-center border-t border-slate-100 bg-white">
        <h3
          className="text-[36px] leading-none font-medium text-[#0f2c6b]"
          style={{ fontFamily: 'Fraunces, serif' }}
        >
          {product.size}
          <span className="text-[16px] text-[#c4a24a] ml-1 font-normal">{product.unit}</span>
        </h3>
        <div className="mt-1 text-[10px] tracking-[0.28em] text-slate-500 uppercase font-medium">
          {product.name} Edition
        </div>
        <p className="mt-3 text-[12.5px] text-slate-500 leading-[1.6] font-light">
          {product.tagline}
        </p>
      </div>
    </div>
  );
};

const BulkCard = () => {
  const lineup = [
    { img: BOTTLES.mini, name: 'Mini', size: '200ml' },
    { img: BOTTLES.mist200, name: 'Mist', size: '200ml', masked: true },
    { img: BOTTLES.plus, name: 'Plus', size: '500ml' },
    { img: BOTTLES.boat, name: 'Boat', size: '1L' },
    { img: BOTTLES.neo, name: 'Neo', size: '1L' },
    { img: BOTTLES.mist, name: 'Mist', size: '1L' },
  ];

  return (
    <div className="group relative flex flex-col lg:flex-row min-h-[420px] overflow-hidden">
      {/* Left: Copy */}
      <div className="lg:w-2/5 bg-gradient-to-br from-[#0a1f4a] via-[#0f2c6b] to-[#0a1f4a] p-8 lg:p-12 relative overflow-hidden flex flex-col justify-between text-white">
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-[#c4a24a]" />
        <div className="absolute -bottom-16 -right-16 w-48 h-48 rounded-full bg-[#c4a24a]/[0.06] blur-2xl" />

        <div className="relative">
          <div className="text-[9px] tracking-[0.3em] text-[#c4a24a] uppercase font-medium mb-4">
            Bulk &mdash; Institutional &amp; Corporate
          </div>
          <h3
            className="text-[42px] lg:text-[52px] leading-[1.05] font-medium text-white mb-4"
            style={{ fontFamily: 'Fraunces, serif', letterSpacing: '-0.02em' }}
          >
            <span className="italic text-[#c4a24a] font-light">Bespoke</span> volumes for the enterprise.
          </h3>
          <p className="text-[14px] text-white/70 leading-[1.75] font-light max-w-md">
            Custom-branded pallet quantities across every SKU &mdash; ideal for hospitality, corporate events, airlines, retail chains and institutional supply.
          </p>
        </div>

        <div className="relative mt-6 pt-6 border-t border-white/15 flex items-center gap-6">
          <div>
            <div className="text-[10px] tracking-[0.3em] text-white/50 uppercase mb-1">SKU coverage</div>
            <div
              className="text-[28px] font-medium text-white"
              style={{ fontFamily: 'Fraunces, serif' }}
            >
              6 <span className="text-[#c4a24a] italic font-light">variants</span>
            </div>
          </div>
          <div className="h-10 w-[1px] bg-white/20" />
          <div>
            <div className="text-[10px] tracking-[0.3em] text-white/50 uppercase mb-1">Custom branding</div>
            <div
              className="text-[28px] font-medium text-white"
              style={{ fontFamily: 'Fraunces, serif' }}
            >
              <span className="italic text-[#c4a24a] font-light">Available</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right: Bottle showcase */}
      <div className="lg:w-3/5 relative flex items-end justify-center overflow-hidden">
        {/* Cream gradient bg matching cards */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse at 50% 60%, #f5efdf 0%, #eae0c5 55%, #d9cca8 100%)',
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse at 50% 20%, rgba(255,240,200,0.4) 0%, transparent 60%)',
          }}
        />

        <div className="relative flex items-end justify-center gap-1 lg:gap-2 pb-12 pt-10 px-6 w-full">
          {lineup.map((item, i) => {
            const heights = ['h-40', 'h-48', 'h-52', 'h-64', 'h-56', 'h-52'];
            return (
              <div key={i} className="flex flex-col items-center group/item">
                <img
                  src={item.img}
                  alt={`${item.name} ${item.size}`}
                  className={`${heights[i]} w-auto object-contain drop-shadow-[0_15px_25px_rgba(15,44,107,0.25)] group-hover/item:scale-105 group-hover/item:-translate-y-2 transition-transform duration-500`}
                  style={
                    item.masked
                      ? { WebkitMaskImage: bottleMask, maskImage: bottleMask }
                      : undefined
                  }
                />
                <div className="mt-2 text-center">
                  <div
                    className="text-[11px] font-medium text-[#0f2c6b]"
                    style={{ fontFamily: 'Fraunces, serif' }}
                  >
                    {item.name}
                  </div>
                  <div className="text-[8.5px] tracking-[0.2em] text-[#0f2c6b]/60 uppercase">
                    {item.size}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Ground reflection */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 w-2/3 h-4 bg-black/15 rounded-full blur-md" />
      </div>
    </div>
  );
};

export default Products;
